import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Partners.css';

const Partners = () => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return (
        <div className={`page partners-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Hero */}
            <section className="page-hero">
                <div className="container">
                    <span className="label">For Integration Partners</span>
                    <h1>Expand your capability without expanding your headcount.</h1>
                    <p className="page-hero-subtitle">
                        A partnership model designed for skilled installation companies
                        who want to offer automation without the engineering overhead.
                    </p>
                    <div className="page-hero-cta">
                        <Link to="/contact" className="btn btn-primary">Start a conversation</Link>
                        <Link to="/partner-program" className="btn btn-secondary">Join field partner waitlist</Link>
                    </div>
                </div>
            </section>

            {/* Opportunity */}
            <section className="opportunity-section">
                <div className="container">
                    <div className="opportunity-content">
                        <h2>The opportunity is clear. The execution doesn't have to be yours.</h2>
                        <p>
                            Your clients are asking for automation. You know the installation side
                            backward and forward. But system engineering, programming, commissioning,
                            and documentation? That's a different skill set—and a different business model.
                        </p>
                        <p>
                            We provide the engineering, documentation, and project oversight. You provide
                            the field execution. Your clients get a premium result, and you earn revenue
                            from projects you'd otherwise walk away from.
                        </p>
                    </div>
                </div>
            </section>

            {/* Model */}
            <section className="model-section">
                <div className="container">
                    <span className="label">Partnership Model</span>
                    <h2>Clear responsibilities. No ambiguity.</h2>

                    <div className="model-grid">
                        <div className="model-column">
                            <h3>We handle</h3>
                            <ul>
                                <li>System architecture and design</li>
                                <li>Documentation and drawing packages</li>
                                <li>Equipment specification and procurement</li>
                                <li>Programming and commissioning</li>
                                <li>Client training and handoff</li>
                                <li>Ongoing system support</li>
                            </ul>
                        </div>
                        <div className="model-column">
                            <h3>You handle</h3>
                            <ul>
                                <li>Field installation per our drawings</li>
                                <li>Wire pulling and termination</li>
                                <li>Device mounting and rough-in</li>
                                <li>Equipment rack assembly</li>
                                <li>Your existing client relationship</li>
                                <li>Local permitting and inspection</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="benefits-section">
                <div className="container">
                    <span className="label">Why Partner</span>
                    <h2>Benefits that compound over time</h2>

                    <div className="benefits-grid">
                        <div className="benefit-card">
                            <h3>Revenue expansion</h3>
                            <p>
                                Add automation projects to your portfolio without hiring engineers
                                or programmers. We bring the skill set; you bring the local presence.
                            </p>
                        </div>
                        <div className="benefit-card">
                            <h3>Premium positioning</h3>
                            <p>
                                Offer your clients an engineering-first approach with documentation
                                that sets you apart from competitors who wing it.
                            </p>
                        </div>
                        <div className="benefit-card">
                            <h3>Professional development</h3>
                            <p>
                                Your team learns from working with our drawings and processes.
                                Over time, your installation quality improves across all your projects.
                            </p>
                        </div>
                        <div className="benefit-card">
                            <h3>No inventory risk</h3>
                            <p>
                                We specify and procure equipment. You don't need to stock expensive
                                automation products or manage vendor relationships.
                            </p>
                        </div>
                        <div className="benefit-card">
                            <h3>Reduced callbacks</h3>
                            <p>
                                Properly engineered and documented systems have fewer issues.
                                That means fewer truck rolls and fewer uncomfortable client conversations.
                            </p>
                        </div>
                        <div className="benefit-card">
                            <h3>Scale without complexity</h3>
                            <p>
                                Take on more automation projects without the overhead of building
                                an engineering department. We scale with you.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Concerns */}
            <section className="concerns-section">
                <div className="container">
                    <span className="label">Questions you might have</span>

                    <div className="concerns-list">
                        <div className="concern-item">
                            <h3>"Will you try to take my clients?"</h3>
                            <p>
                                No. We work through you, not around you. Your client relationship
                                stays yours. We're the invisible engineering partner behind your
                                brand.
                            </p>
                        </div>
                        <div className="concern-item">
                            <h3>"What's the minimum project size?"</h3>
                            <p>
                                We focus on projects where proper engineering adds clear value—typically
                                $50K+ in automation scope. Below that, the engineering overhead
                                doesn't make economic sense for either of us.
                            </p>
                        </div>
                        <div className="concern-item">
                            <h3>"How do you make money?"</h3>
                            <p>
                                We earn through the engineering and programming scope. Our pricing
                                is transparent and included in the project budget from day one. No
                                hidden fees or surprise line items.
                            </p>
                        </div>
                        <div className="concern-item">
                            <h3>"What if my team isn't experienced enough?"</h3>
                            <p>
                                Our documentation is designed to be followed by competent technicians,
                                even without prior automation experience. We also provide field support
                                for the first few projects to build confidence.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Requirements */}
            <section className="requirements-section">
                <div className="container">
                    <span className="label">Requirements</span>
                    <h2>What we look for in a partner</h2>
                    <p className="requirements-intro">
                        We're selective about partnerships because our name is on every system
                        we engineer. Here's what matters to us:
                    </p>

                    <div className="requirements-grid">
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Licensed and insured electrical or low-voltage contractor</p>
                        </div>
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Minimum 3 years in residential or commercial installation</p>
                        </div>
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Willingness to follow engineered drawings precisely</p>
                        </div>
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Commitment to quality documentation and photo records</p>
                        </div>
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Professional communication with design teams and clients</p>
                        </div>
                        <div className="requirement">
                            <span className="req-icon">✓</span>
                            <p>Geographic presence in markets we serve or want to expand into</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="page-cta">
                <div className="container">
                    <h2>Ready to explore a partnership?</h2>
                    <p>
                        Let's start with a conversation about your business, your market,
                        and how we might work together.
                    </p>
                    <div className="page-hero-cta" style={{ justifyContent: 'center' }}>
                        <Link to="/contact" className="btn btn-primary">Get in touch</Link>
                        <Link to="/partner-program" className="btn btn-light-outline">Join field partner waitlist</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Partners;
